import { useEffect, useRef, useState } from "react";

import { fetchListRfqItems, fetchRfqStrategyParams } from "../../http/api";
import { PROTOCOL_TYPES } from "../../Constants/AppConstants";
import { useListRfqStore } from "../../Store/listRfqStore";
import {
  buildPrefillRows,
  buildPrefillSharedFields,
  isListPrefillCandidate,
} from "./listRfqPrefill";

/**
 * Fills the List Order tab with the list a re-initiated order belonged to.
 *
 * In plain terms: the trader clicked Re-Initiate on one bond of a list, opening the
 * Bond Order tab first; opening List Order restores that list's bonds and enquiry settings.
 *
 * <p><b>Where it sits, and why.</b> It is called from OrderEntryCore rather than from
 * ListRfqTab because OrderEntryCore stays mounted for as long as the modal is open, while the
 * tab panels unmount on every tab switch (TabPanel renders `value === index && children`).
 * A ref in a component that unmounts would forget it had already prefilled, and returning to
 * the tab would wipe whatever the trader had just typed. Held in a ref rather than in the
 * store for the same reason from the other end: "Clear All" resets the store, and a flag
 * living there would let the next tab switch silently refill a grid the trader had just
 * emptied on purpose.
 *
 * <p>Reinitiation loads an associated list in the background (`active`), while an
 * ordinary single bond never pays for a list fetch it has no use for. The Bond Order tab is
 * prefilled by the existing path either way and is not touched by any of this -
 * Pages/Order.jsx handleResubmitRequest still does that, unchanged.
 *
 * <p><b>Validation.</b> Prefilled rows are put through validateRowCusip, the same lookup the
 * grid runs when a trader blurs an identifier cell. It is not optional: rows arrive at
 * ROW_STATUS.IDLE and listRfqPayload.validateListRfq refuses to submit any row that is not
 * VALID, and the lookup is also the only thing that supplies bondInventoryId, which the
 * payload carries per bond. The single-bond ticket does exactly the same with a re-initiated
 * order - ActualOrderTab's validateCusip effect fires off actualOrderData.cusip the moment the
 * resubmit prefills it (ActualOrderTab.jsx ~line 776), so neither screen leaves a prefilled
 * identifier unchecked.
 *
 * <p>One consequence is worth being explicit about: that lookup overwrites the price with the
 * security master's, and raises a quantity that sits below the bond's minimum. Both are
 * existing behaviour of validateRowCusip, and both match the single-bond ticket, which
 * overwrites a re-initiated order's price the same way (ActualOrderTab.jsx ~line 583,
 * `hasUsablePrice(data.price) && !readOnly`). So the prefilled price is a starting point, not
 * a promise that the old order's price survived - exactly as on the Bond Order tab.
 *
 * @param {string}  args.token   auth token, as every other fetch on this screen takes
 * @param {object}  args.order   the order the modal was opened with (the re-initiated blotter row)
 * @param {boolean} args.active  load the list now, either in the background or on its tab
 * @param {boolean} args.enabled is the List Order tab available at all in this mode?
 */
export const useListRfqPrefill = ({ token, order, active, enabled = true }) => {
  const [loading, setLoading] = useState(false);
  // The order object is rebuilt by the caller on some renders, so the effect below keys off
  // its id and reads the object itself through this ref. Declared before that effect so it is
  // already up to date by the time the effect runs.
  const orderRef = useRef(order);
  useEffect(() => {
    orderRef.current = order;
  });

  // Which order we have already prefilled from. Null means "not yet", and it is reset to null
  // when a fetch finds nothing or is interrupted, so opening the tab again retries rather than leaving the
  // trader with a grid that stayed blank because of one failed call.
  const prefilledOrderIdRef = useRef(null);

  const orderId = order?.orderId;
  const listId = order?.listId ?? order?.mktxListId ?? order?.tradingSessionSubId;
  const protocol = order?.protocol;

  useEffect(() => {
    if (!enabled || !active || !token) return undefined;

    const sourceOrder = orderRef.current;
    if (!isListPrefillCandidate(sourceOrder)) return undefined;
    if (prefilledOrderIdRef.current === sourceOrder.orderId) return undefined;

    // Claimed before the await, not after it, so a double mount cannot start two fetches and
    // apply two sets of rows.
    prefilledOrderIdRef.current = sourceOrder.orderId;
    setLoading(true);

    let cancelled = false;
    let applied = false;

    const prefill = async () => {
      // Returns [] on any failure rather than throwing (see http/api.js), so a list that
      // cannot be read leaves the trader with the ordinary blank grid instead of a broken tab.
      const savedItems = await fetchListRfqItems(token, sourceOrder.orderId);
      const items = sourceOrder.protocol === PROTOCOL_TYPES.MARKETAXESS
        ? await Promise.all(savedItems.map(async (item) => {
            if (!item.rfqId) return item;
            const params = await fetchRfqStrategyParams(token, item.rfqId);
            if (!Array.isArray(params)) throw new Error("Unable to load saved RFQ strategy parameters");
            return {
              ...item,
              strategyParameters: Object.fromEntries((params || [])
                .filter((param) => param.paramName && param.paramValue != null)
                .map((param) => {
                  const value = String(param.paramValue).trim();
                  const upper = value.toUpperCase();
                  return [param.paramName, upper === "YES" || upper === "Y" ? "1"
                    : upper === "NO" || upper === "N" ? "0" : value];
                })),
            };
          }))
        : savedItems;
      if (cancelled) return;

      const rows = buildPrefillRows(items, sourceOrder, { reinitiate: true });
      if (!rows.length) {
        prefilledOrderIdRef.current = null;
        return;
      }

      applied = true;
      useListRfqStore.getState().applyListPrefill({
        sharedFields: buildPrefillSharedFields(sourceOrder, items),
        rows,
        defaultDealers: true,
      });

      // Read off the store rather than captured above: the rows are already in place, and
      // this is the same action the grid calls on blur.
      const { validateRowCusip } = useListRfqStore.getState();
      await Promise.all(rows.map((row) => validateRowCusip(token, row.rowId)));
    };

    prefill().catch((error) => {
      if (cancelled) return;
      // Do not apply a partially restored MA list when one bond's parameters failed to load.
      // Opening the tab again should retry instead of treating the failed fetch as completed.
      if (prefilledOrderIdRef.current === sourceOrder.orderId) prefilledOrderIdRef.current = null;
      console.error("Unable to restore list RFQ:", error);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
      setLoading(false);
      if (!applied && prefilledOrderIdRef.current === sourceOrder.orderId) {
        prefilledOrderIdRef.current = null;
      }
    };
  }, [enabled, active, token, orderId, listId, protocol]);
  return loading;
};

export default useListRfqPrefill;
